import React, { useState, useEffect } from 'react';
import { Icons } from '../../shared/ui';
const { X: XMarkIcon, Edit: PencilIcon, Lock: KeyIcon, Check: CheckIcon, XCircle: XCircleIcon, User: UserCircleIcon, Mail: EnvelopeIcon, Phone: PhoneIcon, Shield: ShieldCheckIcon, Alert: ShieldExclamationIcon, Calendar: CalendarIcon, Copy: DocumentDuplicateIcon } = Icons;
import axios from 'axios';
import toast from 'react-hot-toast';

const UserDetailModal = ({ user, isOpen, onClose, onUpdate }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const [renterProfile, setRenterProfile] = useState(null);

  const [formData, setFormData] = useState({
    email: '',
    first_name: '',
    last_name: '',
    phone: '',
    role: '',
    is_active: true
  });

  useEffect(() => {
    if (user) {
      setFormData({
        email: user.email || '',
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        phone: user.phone || '',
        role: user.role || 'renter',
        is_active: user.is_active !== undefined ? user.is_active : true
      });
      setIsEditing(false);
      setShowPasswordReset(false);
      setNewPassword('');
      setConfirmPassword('');
      setRenterProfile(null);

      // Fetch renter profile for emergency contact
      if (user.role === 'renter') {
        axios.get(`/api/users/${user.id}/renter-profile`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        }).then(res => setRenterProfile(res.data)).catch(() => setRenterProfile(null));
      }
    }
  }, [user]);

  if (!isOpen || !user) return null;

  const handleUpdate = async () => {
    try {
      setLoading(true);
      const response = await axios.put(
        `/api/users/${user.id}/admin-update`,
        formData,
        {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        }
      );

      toast.success('User updated successfully');
      setIsEditing(false);
      if (onUpdate) onUpdate(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordReset = async () => {
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (newPassword.length < 8) {
      toast.error('Password must be at least 8 characters long');
      return;
    }

    try {
      setLoading(true);
      await axios.post(
        `/api/users/${user.id}/admin-reset-password`,
        { password: newPassword },
        {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        }
      );

      toast.success('Password reset successfully');
      setShowPasswordReset(false);
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="fixed inset-0 bg-stone-500 bg-opacity-75 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4 rounded-t-lg">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-3">
              <UserCircleIcon className="h-8 w-8 text-white" />
              <div>
                <h2 className="text-xl font-bold text-white">User Details</h2>
                <p className="text-teal-100 text-sm">{user.username}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:text-stone-200 transition-colors"
            >
              <XMarkIcon size={24} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Status and Role */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-4">
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                user.is_active
                  ? 'bg-green-100 text-green-800'
                  : 'bg-red-100 text-red-800'
              }`}>
                {user.is_active ? 'Active' : 'Inactive'}
              </span>
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                user.role === 'admin'
                  ? 'bg-purple-100 text-purple-800'
                  : user.role === 'manager'
                  ? 'bg-teal-100 text-teal-800'
                  : 'bg-stone-100 text-stone-800'
              }`}>
                {user.role?.charAt(0).toUpperCase() + user.role?.slice(1)}
              </span>
            </div>

            {!isEditing && (
              <div className="flex space-x-2">
                <button
                  onClick={() => setIsEditing(true)}
                  className="flex items-center gap-2 px-3 py-1.5 bg-teal-600 text-white rounded-md hover:bg-teal-700 transition-colors"
                >
                  <PencilIcon size={16} />
                  Edit
                </button>
                <button
                  onClick={() => setShowPasswordReset(!showPasswordReset)}
                  className="flex items-center gap-2 px-3 py-1.5 bg-yellow-600 text-white rounded-md hover:bg-yellow-700 transition-colors"
                >
                  <KeyIcon size={16} />
                  Reset Password
                </button>
              </div>
            )}
          </div>

          {/* User Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Basic Information */}
            <div className="space-y-4">
              <h3 className="font-semibold text-stone-900 border-b pb-2">Basic Information</h3>

              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">
                  <UserCircleIcon size={16} className="inline mr-1" />
                  Username
                </label>
                <div className="flex items-center justify-between">
                  <p className="text-stone-900">{user.username}</p>
                  <button
                    onClick={() => copyToClipboard(user.username)}
                    className="text-stone-400 hover:text-stone-600"
                  >
                    <DocumentDuplicateIcon size={16} />
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">
                  <EnvelopeIcon size={16} className="inline mr-1" />
                  Email
                </label>
                {isEditing ? (
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-3 py-2 border border-stone-300 rounded-md focus:outline-none focus:ring-teal-500 focus:border-teal-500"
                  />
                ) : (
                  <p className="text-stone-900">{user.email || 'Not provided'}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">
                  <PhoneIcon size={16} className="inline mr-1" />
                  Phone
                </label>
                {isEditing ? (
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full px-3 py-2 border border-stone-300 rounded-md focus:outline-none focus:ring-teal-500 focus:border-teal-500"
                  />
                ) : (
                  <p className="text-stone-900">{user.phone || 'Not provided'}</p>
                )}
              </div>
            </div>

            {/* Personal Information */}
            <div className="space-y-4">
              <h3 className="font-semibold text-stone-900 border-b pb-2">Personal Information</h3>

              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">
                  First Name
                </label>
                {isEditing ? (
                  <input
                    type="text"
                    value={formData.first_name}
                    onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                    className="w-full px-3 py-2 border border-stone-300 rounded-md focus:outline-none focus:ring-teal-500 focus:border-teal-500"
                  />
                ) : (
                  <p className="text-stone-900">{user.first_name || 'Not provided'}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">
                  Last Name
                </label>
                {isEditing ? (
                  <input
                    type="text"
                    value={formData.last_name}
                    onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                    className="w-full px-3 py-2 border border-stone-300 rounded-md focus:outline-none focus:ring-teal-500 focus:border-teal-500"
                  />
                ) : (
                  <p className="text-stone-900">{user.last_name || 'Not provided'}</p>
                )}
              </div>

              {isEditing && (
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">
                    <ShieldCheckIcon size={16} className="inline mr-1" />
                    Role
                  </label>
                  <select
                    value={formData.role}
                    onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                    className="w-full px-3 py-2 border border-stone-300 rounded-md focus:outline-none focus:ring-teal-500 focus:border-teal-500"
                  >
                    <option value="renter">Renter</option>
                    <option value="manager">Manager</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
              )}
            </div>
          </div>

          {/* Emergency Contact (Renters only) */}
          {user.role === 'renter' && (
            <div className="mt-6 pt-6 border-t">
              <h3 className="font-semibold text-stone-900 mb-4 flex items-center gap-2">
                <ShieldExclamationIcon size={20} className="text-amber-600" />
                Emergency Contact
              </h3>
              {renterProfile && (renterProfile.emergency_contact_name || renterProfile.emergency_contact_phone) ? (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <span className="block text-sm text-stone-500">Name</span>
                    <span className="text-stone-900 font-medium">{renterProfile.emergency_contact_name || 'N/A'}</span>
                  </div>
                  <div>
                    <span className="block text-sm text-stone-500">Phone</span>
                    <span className="text-stone-900">{renterProfile.emergency_contact_phone || 'N/A'}</span>
                  </div>
                  <div>
                    <span className="block text-sm text-stone-500">Relationship</span>
                    <span className="text-stone-900 capitalize">{renterProfile.emergency_contact_relationship || 'N/A'}</span>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-stone-400">No emergency contact provided</p>
              )}
            </div>
          )}

          {/* Account Information */}
          <div className="mt-6 pt-6 border-t">
            <h3 className="font-semibold text-stone-900 mb-4">Account Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-stone-600">User ID:</span>
                <span className="ml-2 font-medium">{user.id}</span>
              </div>
              <div>
                <span className="text-stone-600">Created:</span>
                <span className="ml-2 font-medium">{formatDate(user.created_at)}</span>
              </div>
              <div>
                <span className="text-stone-600">Last Updated:</span>
                <span className="ml-2 font-medium">{formatDate(user.updated_at)}</span>
              </div>
              {isEditing && (
                <div>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.is_active}
                      onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                      className="mr-2"
                    />
                    <span className="text-stone-700">Account Active</span>
                  </label>
                </div>
              )}
            </div>
          </div>

          {/* Password Reset Section */}
          {showPasswordReset && (
            <div className="mt-6 pt-6 border-t bg-yellow-50 -mx-6 px-6 pb-6">
              <h3 className="font-semibold text-stone-900 mb-4">Reset Password</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">
                    New Password
                  </label>
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="w-full px-3 py-2 border border-stone-300 rounded-md focus:outline-none focus:ring-yellow-500 focus:border-yellow-500"
                    placeholder="Minimum 8 characters"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">
                    Confirm Password
                  </label>
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full px-3 py-2 border border-stone-300 rounded-md focus:outline-none focus:ring-yellow-500 focus:border-yellow-500"
                    placeholder="Re-enter password"
                  />
                </div>
              </div>
              {newPassword && confirmPassword && newPassword !== confirmPassword && (
                <p className="text-red-600 text-sm mt-2">Passwords do not match</p>
              )}
              <div className="flex justify-end mt-4 space-x-2">
                <button
                  onClick={() => {
                    setShowPasswordReset(false);
                    setNewPassword('');
                    setConfirmPassword('');
                  }}
                  className="px-4 py-2 text-stone-700 bg-stone-200 rounded-md hover:bg-stone-300"
                >
                  Cancel
                </button>
                <button
                  onClick={handlePasswordReset}
                  disabled={loading || !newPassword || !confirmPassword || newPassword !== confirmPassword}
                  className="px-4 py-2 bg-yellow-600 text-white rounded-md hover:bg-yellow-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Reset Password
                </button>
              </div>
            </div>
          )}

          {/* Edit Actions */}
          {isEditing && (
            <div className="mt-6 pt-6 border-t flex justify-end space-x-2">
              <button
                onClick={() => {
                  setIsEditing(false);
                  setFormData({
                    email: user.email || '',
                    first_name: user.first_name || '',
                    last_name: user.last_name || '',
                    phone: user.phone || '',
                    role: user.role || 'renter',
                    is_active: user.is_active !== undefined ? user.is_active : true
                  });
                }}
                className="px-4 py-2 text-stone-700 bg-stone-200 rounded-md hover:bg-stone-300"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                onClick={handleUpdate}
                className="px-4 py-2 bg-teal-600 text-white rounded-md hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Saving...
                  </>
                ) : (
                  <>
                    <CheckIcon size={16} />
                    Save Changes
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserDetailModal;